import { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ReplayIcon from '@mui/icons-material/Replay'
import Markdown from '../Markdown'
import { bodyText, colours } from '../player/playerStyles'
import Feedback from './Feedback'
import useBlockState from './useBlockState'

const QUALITY = {
  best: { tone: 'good', label: 'Best response' },
  acceptable: { tone: 'info', label: 'Acceptable, but there is a better option' },
  poor: { tone: 'bad', label: 'Not the best response' },
}

// Follows the choices taken from the start node. Returns each step and the
// node the learner is now on (null once an ending choice has been made).
const walk = (block, path) => {
  const steps = []
  let nodeId = block.start
  for (const choiceId of path) {
    const node = block.nodes.find((candidate) => candidate.id === nodeId)
    const choice = node?.choices.find((candidate) => candidate.id === choiceId)
    if (!choice) break
    steps.push({ node, choice })
    nodeId = choice.next
    if (nodeId === null) break
  }
  return { steps, node: nodeId === null ? null : block.nodes.find((candidate) => candidate.id === nodeId) }
}

const BranchingScenario = ({ block, initialState, onStateChange }) => {
  const [state, update] = useBlockState({ path: [], finished: false, ...initialState }, onStateChange)
  // 'choose' → 'consequence' → (next node: 'choose' | end: 'debrief')
  const path = state.path ?? []
  const { steps, node } = walk(block, path)
  const last = steps[steps.length - 1]
  const [stage, setStage] = useState(() => {
    if (state.finished) return 'debrief'
    // Left on the final consequence earlier in this session.
    return steps.length > 0 && !node ? 'consequence' : 'choose'
  })
  const stageRef = useRef(null)
  const firstRender = useRef(true)

  // The buttons just pressed disappear, so move focus to what replaced them.
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    stageRef.current?.focus()
  }, [stage, path.length])

  const choose = (choiceId) => {
    update({ path: [...path, choiceId], finished: false })
    setStage('consequence')
  }

  const carryOn = () => {
    if (node) {
      setStage('choose')
      return
    }
    // The path sent is exactly the choices taken from the start node to the
    // ending choice: the server walks it to check it.
    update({ path, finished: true })
    setStage('debrief')
  }

  const restart = () => {
    update({ path: [], finished: false })
    setStage('choose')
  }

  return (
    <Box sx={{ maxWidth: '74ch' }}>
      <Markdown sx={{ ...bodyText, mb: 2 }}>{block.intro}</Markdown>

      <Box ref={stageRef} tabIndex={-1} role="group" aria-label="Scenario step" sx={{ outline: 'none' }}>
        {stage === 'choose' && node && (
          <Box sx={{ p: { xs: 1.5, sm: 2 }, border: `2px solid ${colours.border}`, borderRadius: 1.5 }}>
            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: colours.muted, mb: 1 }}>
              Decision {steps.length + 1}
            </Typography>
            <Markdown sx={bodyText}>{node.text}</Markdown>
            <Typography component="h3" sx={{ fontSize: '1.0625rem', fontWeight: 700, mt: 2, mb: 1 }}>
              What do you do?
            </Typography>
            <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {node.choices.map((choice) => (
                <li key={choice.id}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => choose(choice.id)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', fontWeight: 400, fontSize: '1.0625rem', lineHeight: 1.5, py: 1.25, borderColor: colours.border, color: colours.text }}
                  >
                    {choice.text}
                  </Button>
                </li>
              ))}
            </Stack>
          </Box>
        )}

        {stage === 'consequence' && last && (
          <Box>
            <Typography sx={{ ...bodyText, mb: 1 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                You chose:{' '}
              </Box>
              {last.choice.text}
            </Typography>
            <Feedback tone={QUALITY[last.choice.quality]?.tone} label={QUALITY[last.choice.quality]?.label}>
              {last.choice.consequence}
            </Feedback>
            <Button variant="contained" onClick={carryOn} sx={{ mt: 2 }}>
              {node ? 'Continue' : 'Continue to the debrief'}
            </Button>
          </Box>
        )}

        {stage === 'debrief' && (
          <Box>
            <Typography component="h3" sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 1 }}>
              Debrief
            </Typography>
            <Markdown sx={bodyText}>{block.debrief}</Markdown>
            {steps.length > 0 && (
              <>
                <Typography component="h3" sx={{ fontSize: '1.0625rem', fontWeight: 700, mt: 2.5, mb: 1 }}>
                  Your choices
                </Typography>
                <Box component="ol" sx={{ ...bodyText, m: 0, pl: 3 }}>
                  {steps.map(({ choice }) => (
                    <Box component="li" key={choice.id} sx={{ mb: 0.75 }}>
                      {choice.text}
                      <Box component="span" sx={{ display: 'block', fontWeight: 700, color: colours.muted }}>
                        {QUALITY[choice.quality]?.label}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
            <Button variant="outlined" startIcon={<ReplayIcon />} onClick={restart} sx={{ mt: 2.5 }}>
              Try the scenario again
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default BranchingScenario
